<template>
    <div class="ls-customer-search-view--container">
        <div class="ls-customer-search-view--search">
            <LsTopLevelSearchInput v-model="input" @enter="searchCustomers" />
        </div>
        <div class="ls-customer-search-view--content">
            <LsContentPlaceholder
                v-if="showPlaceholder"
                :message="placeholderData.message"
                :image="placeholderData.image"
            />
            <LsCustomerTable
                v-if="showTable"
                :customers="customers"
                :pagination="pagination"
                :change-page="changePage"
                test-id="table"
            />
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import { lsErrorService } from 'ls-vue-common';
import Customers from '@/graphql/queries/Customers.gql';
import LsCustomerTable from '@/components/LsCustomerTable/LsCustomerTable.vue';
import LsContentPlaceholder from '@/components/LsContentPlaceholder/LsContentPlaceholder.vue';
import LsTopLevelSearchInput from '@/components/LsTopLevelSearchInput/LsTopLevelSearchInput.vue';

export default {
    name: 'CustomerSearchView',
    components: {
        LsCustomerTable,
        LsContentPlaceholder,
        LsTopLevelSearchInput,
    },
    data() {
        return {
            pagination: {
                totalItems: 0,
                currentPage: 0,
                itemsPerPage: 0,
            },
            q: '',
            input: '',
            customers: null,
            loading: 0,
            skipQuery: true,
            isPageChange: false,
        };
    },
    computed: {
        showPlaceholder() {
            return !_.size(this.customers) || this.loading;
        },
        showTable() {
            return _.size(this.customers);
        },
        placeholderData() {
            if (this.loading) {
                return {
                    message: 'Loading...',
                    image: 'spinner',
                };
            }
            if (!_.size(this.customers) && this.customers) {
                return {
                    message: 'No search results were found',
                    image: 'search',
                };
            }
            return {
                message: 'Search by name, address, email or phone.',
                image: 'search',
            };
        },
    },
    watch: {
        '$route.query'() {
            this.searchFromRouteQuery();
        },
        deep: true,
    },
    mounted() {
        this.searchFromRouteQuery();
    },
    apollo: {
        customers: {
            query: Customers,
            variables() {
                return {
                    q: this.q,
                    page: this.pagination.currentPage,
                };
            },
            // disable automatically running query on mount
            skip() {
                return this.skipQuery;
            },
            result({ data, loading }) {
                if (!loading) {
                    const { currentPage, totalItems, itemsPerPage } = data.customers.pagination;
                    this.pagination = { currentPage, totalItems, itemsPerPage };

                    this.customers = _.map(data.customers.customers, (customer) => {
                        // TODO: fix later, temporarily handle case where customer has no properties
                        if (!_.size(customer.properties)) {
                            customer.properties.push({
                                id: null,
                            });
                        }
                        return customer;
                    });
                    this.$apollo.queries.customers.skip = true;
                }
            },
            error(error) {
                lsErrorService.log({ error });
            },
            loadingKey: 'loading',
        },
    },
    methods: {
        clear() {
            this.customers = null;
        },
        changePage(page) {
            this.input = this.q;
            this.isPageChange = true;
            this.pagination.currentPage = page;
            this.searchCustomers();
        },
        searchFromRouteQuery() {
            if (!_.size(this.$route.query)) {
                this.customers = null;
                this.input = '';
                return;
            }

            const { query, page } = this.$route.query;

            this.q = query;
            this.input = query;
            this.pagination.currentPage = _.parseInt(page);

            this.runApolloQuery();
        },
        searchCustomers() {
            if (!this.isPageChange) {
                this.q = this.input;
                this.customers = null;
                this.pagination = {
                    totalItems: 0,
                    currentPage: 1,
                    itemsPerPage: 0,
                };
            } else {
                this.isPageChange = false;
            }

            if (!this.input) {
                this.customers = null;
                return;
            }

            const { query, page } = this.$route.query;

            if (query !== this.q || _.toNumber(page) !== this.pagination.currentPage) {
                this.$router.push({
                    query: {
                        query: this.q,
                        page: this.pagination.currentPage,
                    },
                });
                // apollo query will be triggered here from the watcher if $route.query
                // does not match, so don't run it again below
                return;
            }

            this.runApolloQuery();
        },
        runApolloQuery() {
            this.$apollo.queries.customers.skip = false;
            this.$apollo.queries.customers.refresh();
        },
    },
};
</script>

<style lang="scss" scoped>
.ls-customer-search-view--search {
    width: 50%;
    padding-bottom: 20px;
}

.ls-customer-search-view--content {
    position: relative;
    background-color: $white;
}
</style>
